import { Chip, Box, Link, Button, Grid, Typography } from "@mui/material";
import React, { useState } from "react";
import { DataGridComponent } from "../common/DataGridComponent";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-pro";
import { useSites } from "../../hooks/useSites";
import { AppState, useAppDispatch, useAppSelector } from "../../store";
import {
  setIsEditSite,
  setOpenAddSiteModal,
  setOpenDeleteSite,
  setSiteDetails,
} from "../../store/slices/studiesSlice";
import SiteDetails from "./SiteDetails";
import AddIcon from "@mui/icons-material/Add";
import SiteDialog from "./SiteDialog";
import { useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSiteDialog from "./DeleteSiteDialog";
import { datagridUtils } from "../../utils/datagridUtils";
const SiteList: React.FunctionComponent<any> = (props) => {
  const {
    sitesList,
    paginationModel,
    setPaginationModel,
    allSitesIsLoading,
    rowsCount,
    filterVal,
    setFilterVal,
    getAllSitesFunc,
    sortModel,
    handleSortModelChange,
    codelistData,
  } = props;
  const dispatch = useAppDispatch();
  const params = useParams();
  const openAddSiteModal = useAppSelector(
    (state: AppState) => state.studiesSlice.openAddSiteModal
  );
  const { getSiteDetails } = useSites();

  const [openSiteDetails, setOpenSiteDetails] = useState(false);
  const openDelete = useAppSelector(
    (state: AppState) => state.studiesSlice.openDeleteSite
  );
  const onClickSite = async (data: any) => {
    const response = await getSiteDetails({
      study_id: params?.study_id,
      site_id: data?.site_id,
    });
    if (response?.data) {
      dispatch(setSiteDetails(response?.data));
      setOpenSiteDetails(true);
    }
  };
  const onClickAddSite = () => {
    dispatch(setOpenAddSiteModal(true));
  };
  const onEditClick = async (data: any) => {
    const response = await getSiteDetails({
      study_id: params?.study_id,
      site_id: data?.site_id,
    });
    if (response?.data) {
      dispatch(setSiteDetails(response?.data));
      dispatch(setOpenAddSiteModal(true));
      dispatch(setIsEditSite(true));
    }
  };
  const onDeleteClick = (data: any) => {
    dispatch(setSiteDetails(data));
    dispatch(setOpenDeleteSite(true));
  };

  const countryOptions = datagridUtils(codelistData, "CNTRY")?.valueOptions;
  const siteStatusOptions = datagridUtils(
    codelistData,
    "SITE_STATUS"
  )?.valueOptions;

  const columns: GridColDef[] = [
    {
      field: "site_id",
      headerName: "Site ID",
      minWidth: 100,
      flex: 1,
      renderCell: (params: any) => (
        <Link onClick={() => onClickSite(params?.row)}>
          {params.row.site_id}
        </Link>
      ),
    },
    {
      field: "name",
      headerName: "Site Name",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
    },
    {
      field: "country_value_code",
      headerName: "Country",
      flex: 1,
      type: "singleSelect",
      valueOptions: countryOptions,
      renderCell: (params: any) =>
        countryOptions?.find(
          (e: any) => e.value == params?.row?.country_value_code
        )?.label,
    },
    {
      field: "site_status_value_code",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      type: "singleSelect",
      valueOptions: siteStatusOptions,
      renderCell: (params: any) => (
        <Chip
          label={
            siteStatusOptions?.find(
              (e: any) => e.value == params?.row?.site_status_value_code
            )?.label
          }
          color="info"
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      width: 90,
      align: "center",
      type: "actions",
      headerName: "Actions",
      sortable: false,
      getActions: (params) => [
        <GridActionsCellItem
          key={params.row.id}
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEditClick(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          key={params.row.id}
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => onDeleteClick(params.row)}
          showInMenu
        />,
      ],
    },
  ];

  return (
    <>
      {openSiteDetails ? (
        <SiteDetails
          setOpenSiteDetails={setOpenSiteDetails}
          getAllSitesFunc={getAllSitesFunc}
          paginationModel={paginationModel}
          filterVal={filterVal}
          codelistData={codelistData}
        />
      ) : (
        <>
          <Grid container spacing={2} mb={2}>
            <Grid item xs>
              <Typography variant="h5">Site List</Typography>
            </Grid>
            <Grid item justifyContent="flex-end">
              <Button
                variant="contained"
                endIcon={<AddIcon />}
                onClick={onClickAddSite}
                id="add_new_site"
                aria-label="Click to add a new Site, which opens a new modal for new site data entry"
              >
                Add New
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ width: "100%", height: "calc(100vh - 304px)" }}>
            <DataGridComponent
              columns={columns}
              rows={sitesList}
              paginationMode="server"
              showQuickFilter
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              loading={allSitesIsLoading}
              rowsCount={rowsCount}
              filterMode="server"
              filterValues={(data: any) => setFilterVal(data)}
              disableRowSelectionOnClick
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              checkboxSelection
              exportFileName="Site_List"
            />
          </Box>
        </>
      )}
      {openAddSiteModal && (
        <SiteDialog
          getAllSitesFunc={getAllSitesFunc}
          paginationModel={paginationModel}
          filterVal={filterVal}
          codelistData={codelistData}
        />
      )}
      {openDelete && (
        <DeleteSiteDialog
          getAllSitesFunc={getAllSitesFunc}
          paginationModel={paginationModel}
          filterVal={filterVal}
          setOpenSiteDetails={setOpenSiteDetails}
        />
      )}
    </>
  );
};

export default SiteList;
